import type { CSSProperties } from 'react';
import type { TourHookResult } from './useTour';

interface TourOverlayProps extends TourHookResult {
  userId?: string;
}

const PADDING = 8;
const OVERLAY_COLOR = 'rgba(0, 0, 0, 0.5)';

export function TourOverlay({
  isActive,
  currentStep,
  steps,
  targetRect,
  goNext,
  goBack,
  skipTour,
  userId,
}: TourOverlayProps) {
  if (!isActive || steps.length === 0) return null;

  const step = steps[currentStep];
  if (!step) return null;

  const isWelcome = step.id === 'welcome';
  const isLastStep = currentStep === steps.length - 1;
  const totalSteps = steps.length - 1; // exclude welcome
  const stepNumber = currentStep; // 1-based for display

  // Don't render tooltip until the target element is found — prevents flash in center while page loads
  if (!isWelcome && !targetRect) {
    return (
      <div
        style={{ position: 'fixed', inset: 0, backgroundColor: OVERLAY_COLOR, zIndex: 9999 }}
      />
    );
  }

  if (isWelcome) {
    return (
      <div
        className="fixed inset-0 flex items-center justify-center"
        style={{ backgroundColor: OVERLAY_COLOR, zIndex: 9999 }}
      >
        <div className="bg-white rounded-xl shadow-2xl p-8 mx-4" style={{ width: 420, maxWidth: '90vw' }}>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h2>
          <p className="text-gray-600 mb-6">{step.body}</p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => skipTour(userId)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Later
            </button>
            <button
              onClick={() => goNext()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Start Tour →
            </button>
          </div>
        </div>
      </div>
    );
  }

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let topEdge = 0;
  let leftEdge = 0;
  let bottomEdge = vh;
  let rightEdge = vw;

  if (targetRect) {
    topEdge = Math.max(0, targetRect.top - PADDING);
    leftEdge = Math.max(0, targetRect.left - PADDING);
    bottomEdge = Math.min(vh, targetRect.bottom + PADDING);
    rightEdge = Math.min(vw, targetRect.right + PADDING);
  }

  const tooltipLeft = targetRect
    ? step.preferLeft
      ? Math.max(12, targetRect.left - 312 - 12)   // to the left of the element
      : Math.max(12, Math.min(targetRect.left, vw - 312))
    : vw / 2 - 150;

  // Always clamp tooltip top within viewport so it never goes off-screen
  // (handles full-viewport elements like timeline/list that have no room below)
  const rawTooltipTop = (() => {
    if (!targetRect) return vh / 2 - 100;
    const below = bottomEdge + 12;
    const above = topEdge - 12 - 220;
    if (below + 220 <= vh) return below;   // fits below element
    if (above >= 10) return above;          // fits above element
    return Math.min(topEdge + 12, vh - 230); // overlap element if no room
  })();
  const clampedTooltipTop = Math.max(10, Math.min(rawTooltipTop, vh - 230));

  const tooltipStyle: CSSProperties = {
    position: 'fixed',
    zIndex: 10000,
    width: 300,
    left: tooltipLeft,
    top: clampedTooltipTop,
  };

  return (
    <>
      {targetRect ? (
        <>
          {/* Top pane */}
          <div
            style={{
              position: 'fixed', top: 0, left: 0, right: 0,
              height: topEdge,
              backgroundColor: OVERLAY_COLOR,
              zIndex: 9999,
            }}
          />
          {/* Bottom pane */}
          <div
            style={{
              position: 'fixed', top: bottomEdge, left: 0, right: 0, bottom: 0,
              backgroundColor: OVERLAY_COLOR,
              zIndex: 9999,
            }}
          />
          {/* Left pane */}
          <div
            style={{
              position: 'fixed', top: topEdge, left: 0,
              width: leftEdge,
              height: bottomEdge - topEdge,
              backgroundColor: OVERLAY_COLOR,
              zIndex: 9999,
            }}
          />
          {/* Right pane */}
          <div
            style={{
              position: 'fixed', top: topEdge, left: rightEdge, right: 0,
              height: bottomEdge - topEdge,
              backgroundColor: OVERLAY_COLOR,
              zIndex: 9999,
            }}
          />
          {/* Highlight ring */}
          <div
            style={{
              position: 'fixed',
              top: topEdge,
              left: leftEdge,
              width: rightEdge - leftEdge,
              height: bottomEdge - topEdge,
              borderRadius: 6,
              boxShadow: '0 0 0 2px #3b82f6, 0 0 0 5px rgba(59,130,246,0.3)',
              zIndex: 9999,
              pointerEvents: 'none',
            }}
          />
        </>
      ) : (
        <div
          style={{
            position: 'fixed', inset: 0,
            backgroundColor: OVERLAY_COLOR,
            zIndex: 9999,
          }}
        />
      )}

      {/* Tooltip */}
      <div style={tooltipStyle} className="bg-white rounded-xl shadow-2xl p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
            Step {stepNumber} of {totalSteps}
          </span>
          <button
            onClick={() => skipTour(userId)}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            End
          </button>
        </div>

        <h3 className="text-sm font-semibold text-gray-900 mb-1">{step.title}</h3>
        <p className="text-xs text-gray-600 mb-3 leading-relaxed">{step.body}</p>

        {/* Dot progress */}
        <div className="flex gap-1 mb-3">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i + 1 === currentStep ? 'w-4 bg-blue-600' : 'w-1.5 bg-gray-200'
              }`}
            />
          ))}
        </div>

        <div className="flex justify-between items-center">
          <button
            onClick={goBack}
            disabled={currentStep <= 1}
            className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            ← Back
          </button>
          <button
            onClick={() => goNext(isLastStep ? userId : undefined)}
            className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            {isLastStep ? 'Finish' : 'Next →'}
          </button>
        </div>
      </div>
    </>
  );
}
